/**
 * Primitives shared by the dwell-time measurements in endVisitTiming and pifTiming.
 *
 * Extracted rather than copied because each of these has a shape that looks arbitrary and is not,
 * so a second hand-rolled version drifts: the clock is injectable because measurement tests cannot
 * use real time, every storage access is wrapped because sessionStorage throws in Safari private
 * mode, and the hidden-time accumulator is seeded from the current visibility rather than assumed
 * visible. Getting any of those subtly different in a second service would produce numbers that
 * disagree with the end-visit ones for reasons nobody could find from a chart.
 *
 * Deliberately not a general utility module: it holds the three things two timing services both
 * need, and no state of its own beyond the test clock.
 */

let clock: () => number = () => Date.now();

/**
 * Test seam. Both timing services read the clock through now(), so one injection here drives all
 * of them - which is why this lives in the shared module rather than being duplicated per service.
 */
export function __setClockForTests(fn: (() => number) | null): void {
  clock = fn || (() => Date.now());
}

export function now(): number {
  return clock();
}

/**
 * sessionStorage rather than localStorage throughout: a measurement belongs to the tab that
 * started it, and a stale entry surviving a browser restart would report a bogus multi-day
 * duration on the next visit.
 *
 * The validator argument is not decoration. A persisted record is parsed back from a previous
 * build of the app, so its shape is an assumption, not a guarantee - a partially-written or
 * since-renamed record has to be discarded rather than fed into arithmetic that would emit a
 * NaN duration.
 */
export function readStoredState<T>(key: string, isValid: (parsed: unknown) => boolean): T | null {
  try {
    const raw = window.sessionStorage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isValid(parsed)) return null;
    return parsed as T;
  } catch {
    return null;
  }
}

export function writeStoredState(key: string, state: unknown | null): void {
  try {
    if (state === null) {
      window.sessionStorage?.removeItem(key);
      return;
    }
    window.sessionStorage?.setItem(key, JSON.stringify(state));
  } catch {
    // sessionStorage can throw (quota, Safari private mode). Timing is best-effort, and must
    // never be the reason a screen fails to open.
  }
}

/**
 * Hidden-tab accounting. Wall clock keeps running while a tab is in the background or the laptop
 * is shut, so any measurement of "how long did someone spend on this" needs the hidden stretches
 * subtracted. Both figures are worth having - the gap between them is itself a signal - so this
 * accumulates hidden time rather than pausing the clock.
 */
export interface HiddenTime {
  hiddenMs: number;
  hiddenSince: number | null;
}

export function newHiddenTime(): HiddenTime {
  return {
    hiddenMs: 0,
    // Seeded from the current visibility rather than assumed visible: a measurement can begin in
    // an already-hidden tab (a restored session, a background prerender), and starting at null
    // would silently count that entire stretch as active time.
    hiddenSince: typeof document !== "undefined" && document.visibilityState === "hidden" ? clock() : null,
  };
}

export function recordHidden(state: HiddenTime, hidden: boolean): void {
  if (hidden) {
    // Guarded: visibilitychange can fire hidden twice without an intervening visible, and
    // restamping would discard the stretch already in progress.
    if (state.hiddenSince === null) state.hiddenSince = clock();
    return;
  }
  if (state.hiddenSince !== null) {
    state.hiddenMs += clock() - state.hiddenSince;
    state.hiddenSince = null;
  }
}

/** Total hidden ms as of `at`, including a hidden stretch still open at that instant. */
export function hiddenMsThrough(state: HiddenTime, at: number): number {
  return state.hiddenMs + (state.hiddenSince === null ? 0 : at - state.hiddenSince);
}
